
import React from "react";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderNetraaVMsAgent () {
    return (
      <div>
        <main className="transition-opacity duration-300">
          <div className="py-4">
    
            {/* Breadcrumbs */}
            <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
              <a className="hover:text-primary hover:underline" href="#">
                Netraa APM
              </a>
              <FiChevronRight />
              <span>Agent Setup Guide</span>
              <FiChevronRight />
              <span>Cloud</span>
              <FiChevronRight />
              <span>Azure</span>
              <FiChevronRight />
              <span className="text-[#1b140d] dark:text-white font-medium">
                VMs
              </span>
            </nav>
    
            {/* Intro */}
            <div className="mb-10 scroll-mt-24" id="overview">
              <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
                Documentation
              </span>
    
              <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                  One Agent Setup Guide
                </span>
              </h1>
    
              <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
                Follow the steps below to install and configure the One Agent on your system.
              </p>
            </div>
    
            {/* Steps */}
            <section className="mb-16">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50">
                  <span className="material-symbols-outlined text-[24px]">list_alt</span>
                </span>
                Steps to Follow
              </h2>
    
              <div className="space-y-6">
    
                {/* Step 1 */}
                <div className="bg-white dark:bg-white/[0.02] border border-border-color dark:border-border-dark rounded-2xl p-6 shadow-sm">
                  <h3 className="font-bold text-text-main dark:text-white mb-2">
                    1. Login with a Sudo User
                  </h3>
                  <p className="text-text-muted dark:text-gray-400">
                    Ensure you are logged in with a user account that has <code>sudo</code> privileges.
                  </p>
                </div>
    
                {/* Step 3 */}
                <div className="bg-white dark:bg-white/[0.02] border border-border-color dark:border-border-dark rounded-2xl p-6 shadow-sm">
                  <h3 className="font-bold text-text-main dark:text-white mb-2">
                    2. Unzip the Package
                  </h3>
    
                  <ul className="list-disc pl-5 space-y-2 text-text-muted dark:text-gray-400">
                    <li>Locate the <code>ONE_AGENT.zip</code> file.</li>
                    <li>
                      Extract it using:
                      <CodeBlock code={`unzip ONE_AGENT.zip`} />
                    </li>
                    <li>Navigate into the extracted folder.</li>
                  </ul>
                </div>
    
                {/* Step 4 */}
                <div className="bg-white dark:bg-white/[0.02] border border-border-color dark:border-border-dark rounded-2xl p-6 shadow-sm">
                  <h3 className="font-bold text-text-main dark:text-white mb-2">
                    3. Run the Install Script
                  </h3>
    
                  <p className="text-text-muted dark:text-gray-400 mb-2">
                    Ensure the script has execution permission:
                  </p>
    
                  <CodeBlock code={`chmod +x install.sh`} />
    
                  <p className="text-text-muted dark:text-gray-400 mt-3 mb-2">
                    Execute the script:
                  </p>
    
                  <CodeBlock code={`./install.sh`} />
                </div>
    
              </div>
            </section>
    
            {/* Notes */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
                  <span className="material-symbols-outlined text-[24px]">info</span>
                </span>
                Notes
              </h2>
    
              <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 p-6 rounded-2xl shadow-sm">
                <ul className="list-disc pl-5 space-y-2 text-sm text-blue-700 dark:text-blue-400">
                  <li>Always run commands as a user with <code>sudo</code> access.</li>
                  <li>If required, the script will prompt for your password during execution.</li>
                </ul>
              </div>
            </section>
    
          </div>
        </main>
      </div>
    );
    }